import { useState } from 'react';

import { isAdmin } from '../../auth/admins';
import { useEffectiveUser } from '../../auth/useAuthUser';
import {
  useCreateOutreachSender,
  useDeleteOutreachSender,
  useOutreachSenders,
  useProvisionSenderSubscription,
  useUpdateOutreachSender,
} from '../../hooks/useOutreach';
import type { OutreachSender } from '../../types/outreach';
import { formatEt } from '../../utils/formatTimestamp';
import styles from '../pages/OutreachPage.module.css';
import { RefreshButton } from './RefreshButton';

/**
 * Senders sub-view: CRUD for the outreach mailbox allowlist.
 *
 * The allowlist mirrors the Exchange Application Access Policy scope group
 * (`outreach-mailboxes`) -- if you add a sender here, also add the mailbox
 * to that EXO group via infrastructure/setup_outreach_app.ps1 or directly.
 * Removing a sender here just disables it for new campaigns; doesn't change
 * EXO state.
 */
export function SendersAdmin() {
  // Honour impersonation: when an admin is "viewing as" a non-admin,
  // the gates should hide just like they would for the impersonated
  // user.
  const { effectiveEmail } = useEffectiveUser();
  const userIsAdmin = isAdmin(effectiveEmail);
  const sendersQ = useOutreachSenders();
  const createMut = useCreateOutreachSender();
  const updateMut = useUpdateOutreachSender();
  const deleteMut = useDeleteOutreachSender();
  const provisionMut = useProvisionSenderSubscription();

  async function handleProvision(s: OutreachSender) {
    try {
      await provisionMut.mutateAsync(s.email);
    } catch (e: unknown) {
      // ApiError carries the FastAPI detail body in `.detail`. Backend
      // puts the underlying Graph error text there ("Graph
      // subscription failed: <Graph response>"), which is the
      // actually-useful diagnostic. Surface it instead of just
      // "API error: 502".
      let msg = e instanceof Error ? e.message : 'unknown';
      const detail = (e as { detail?: unknown })?.detail;
      if (detail && typeof detail === 'object' && 'detail' in detail) {
        const d = (detail as { detail: unknown }).detail;
        if (typeof d === 'string') msg = d;
      } else if (typeof detail === 'string') {
        msg = detail;
      }
      alert(
        `Provision failed: ${msg}\n\n` +
        'Common causes: mailbox not in the outreach-mailboxes EXO group, ' +
        'RAOP block, or Graph throttling. See infrastructure/setup_outreach_app.ps1.',
      );
    }
  }

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({
    email: '',
    display_name: '',
    default_cc: '',
    default_bcc: '',
  });
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setDraft({ email: '', display_name: '', default_cc: '', default_bcc: '' });
    setError(null);
    setShowForm(false);
  }

  async function handleCreate() {
    setError(null);
    if (!draft.email.trim()) {
      setError('Email is required.');
      return;
    }
    try {
      await createMut.mutateAsync({
        email: draft.email.trim(),
        display_name: draft.display_name.trim() || null,
        default_cc: draft.default_cc.trim() || null,
        default_bcc: draft.default_bcc.trim() || null,
      });
      reset();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Create failed.');
    }
  }

  async function toggleActive(s: OutreachSender) {
    try {
      await updateMut.mutateAsync({
        email: s.email,
        body: { active: !s.active },
      });
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Update failed.');
    }
  }

  async function handleDelete(s: OutreachSender) {
    if (!window.confirm(`Delete sender ${s.email}? Campaigns using it will block.`)) {
      return;
    }
    try {
      await deleteMut.mutateAsync(s.email);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Delete failed.');
    }
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Sender mailboxes</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RefreshButton
            onClick={() => sendersQ.refetch()}
            isFetching={sendersQ.isFetching}
            title="Re-fetch senders (subscription expiry, status, etc.)"
          />
          {!showForm && (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => setShowForm(true)}
              disabled={!userIsAdmin}
              title={
                userIsAdmin
                  ? 'Add a new sender mailbox'
                  : 'Admin only — adding a sender requires the Exchange Application Access Policy + Graph subscription provisioning, which only admins can authorize.'
              }
            >
              + Add sender
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div style={{ marginBottom: 16, padding: 12, background: '#FAF9F4', borderRadius: 6 }}>
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.formGrid}>
            <label className={styles.label}>Email</label>
            <input
              className={styles.input}
              type="email"
              placeholder="maria@ember-infra.com"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
            <label className={styles.label}>Display name</label>
            <input
              className={styles.input}
              type="text"
              placeholder="Maria Rengifo"
              value={draft.display_name}
              onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
            />
            <label className={styles.label}>Default CC</label>
            <input
              className={styles.input}
              type="text"
              placeholder="brad@ember-infra.com,ir@ember-infra.com"
              value={draft.default_cc}
              onChange={(e) => setDraft({ ...draft, default_cc: e.target.value })}
            />
            <label className={styles.label}>Default BCC</label>
            <input
              className={styles.input}
              type="text"
              value={draft.default_bcc}
              onChange={(e) => setDraft({ ...draft, default_bcc: e.target.value })}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button type="button" className={styles.button} onClick={reset}>
              Cancel
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={handleCreate}
              disabled={createMut.isPending}
            >
              {createMut.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {sendersQ.isLoading ? (
        <div className={styles.empty}>Loading…</div>
      ) : sendersQ.data && sendersQ.data.length === 0 ? (
        <div className={styles.empty}>
          No senders yet. Add one above (must be a UserMailbox or licensed
          SharedMailbox member of the <code>outreach-mailboxes</code> EXO group).
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Display name</th>
              <th>Status</th>
              <th>Default CC</th>
              <th>Subscription</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sendersQ.data?.map((s) => (
              <tr key={s.email}>
                <td className={styles.code}>{s.email}</td>
                <td>{s.display_name || <em style={{ color: '#999' }}>—</em>}</td>
                <td>
                  <span
                    className={`${styles.statusBadge} ${
                      s.active ? styles.statusActive : styles.statusInactive
                    }`}
                  >
                    {s.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className={styles.code}>
                  {s.default_cc || <em style={{ color: '#999' }}>—</em>}
                </td>
                <td className={styles.code}>
                  {s.graph_subscription_id ? (
                    <>
                      <div>{s.graph_subscription_id.slice(0, 8)}…</div>
                      {s.graph_subscription_expires_at && (
                        <div className={styles.helpText}>
                          expires{' '}
                          {formatEt(s.graph_subscription_expires_at)}
                        </div>
                      )}
                    </>
                  ) : (
                    <em style={{ color: '#B04030' }}>
                      not provisioned · inbound replies dropped
                    </em>
                  )}
                </td>
                <td>
                  <div className={styles.actions}>
                    {s.active && !s.graph_subscription_id && (
                      <button
                        type="button"
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        onClick={() => handleProvision(s)}
                        disabled={provisionMut.isPending || !userIsAdmin}
                        title={
                          userIsAdmin
                            ? 'Provision the Microsoft Graph change-notification subscription so this mailbox can capture inbound replies.'
                            : 'Admin only'
                        }
                      >
                        {provisionMut.isPending ? 'Provisioning…' : 'Provision'}
                      </button>
                    )}
                    <button
                      type="button"
                      className={styles.button}
                      onClick={() => toggleActive(s)}
                      disabled={updateMut.isPending || !userIsAdmin}
                      title={userIsAdmin ? undefined : 'Admin only'}
                    >
                      {s.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      type="button"
                      className={`${styles.button} ${styles.buttonDanger}`}
                      onClick={() => handleDelete(s)}
                      disabled={deleteMut.isPending || !userIsAdmin}
                      title={userIsAdmin ? undefined : 'Admin only'}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
